import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const C = { primary:'#2E7D32', primaryDark:'#1B5E20', border:'#E0E0E0', light:'#F4F6F9', green100:'#E8F5E9', textDark:'#212121', textMid:'#616161', textLight:'#9E9E9E' };
const VERT = C.primary;
function fmt(v) { return v ? Number(v).toLocaleString('fr-FR') : '0'; }
function fmtF(v) { return (v ? Number(v).toLocaleString('fr-FR') : '0') + ' F'; }
function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }); }
function fmtP(v) { return v != null ? Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'; }
function today() { return new Date().toISOString().split('T')[0]; }
function debutMois() { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; }

const PAGE_SIZE = 20;

export default function RapportsPage() {
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const [onglet, setOnglet] = useState('ca');
  const [chargement, setChargement] = useState(false);
  const [donnees, setDonnees] = useState(null);
  const [regions, setRegions] = useState([]);
  const [abattoirs, setAbattoirs] = useState([]);
  const [page, setPage] = useState(1);
  const [vueRecap, setVueRecap] = useState('mois');
  const [donneesRegions, setDonneesRegions] = useState(null);
  const role = utilisateur?.role;
  const regionUser = utilisateur?.region || '';
  const isPreposeLimite = role === 'prepose' && regionUser && regionUser.toLowerCase() !== 'dakar';
  const isPreposeDakar = role === 'prepose' && regionUser && regionUser.toLowerCase() === 'dakar';

  const [filtres, setFiltres] = useState({
    date_debut: debutMois(), date_fin: today(), periode: 'jour',
    region: isPreposeLimite ? regionUser : '',
    abattoir_id: ''
  });

  const [filtresRecap, setFiltresRecap] = useState({
    date_debut: debutMois(), date_fin: today(),
    region: isPreposeLimite ? regionUser : ''
  });

  useEffect(() => {
    api.get('/rapports/regions').then(r => setRegions(r.data || [])).catch(() => {});
    api.get('/abattoirs').then(r => setAbattoirs(r.data || [])).catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    setChargement(true); setDonnees(null); setPage(1);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filtres).filter(([,v]) => v)));
      const ep = onglet === 'ca' ? 'ca' : onglet === 'production' ? 'production' : 'caisse';
      const r = await api.get(`/rapports/${ep}?${params}`);
      setDonnees(r.data);
    } catch(err) { alert(err.response?.data?.message || 'Erreur de chargement'); }
    finally { setChargement(false); }
  }, [onglet, filtres]);

  const chargerRecap = useCallback(async () => {
    setChargement(true); setDonneesRegions(null);
    try {
      const annee = (filtresRecap.date_debut || today()).slice(0, 4);
      const f = vueRecap === 'annee'
        ? { date_debut: `${annee}-01-01`, date_fin: `${annee}-12-31` }
        : { date_debut: filtresRecap.date_debut, date_fin: filtresRecap.date_fin };
      if (filtresRecap.region) f.region = filtresRecap.region;
      const params = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([,v]) => v)));
      const r = await api.get(`/rapports/production-regions?${params}`);
      setDonneesRegions(r.data);
    } catch(err) { alert(err.response?.data?.message || 'Erreur de chargement'); }
    finally { setChargement(false); }
  }, [filtresRecap, vueRecap]);

  useEffect(() => {
    if (onglet === 'recap') chargerRecap();
    else charger();
  }, [onglet]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportPDF = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' });
    const heureStr = now.toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' });
    const nom = utilisateur ? utilisateur.prenom + ' ' + utilisateur.nom : 'Inconnu';

    if (onglet === 'recap') {
      if (!donneesRegions || !lignesRegions.length) return;
      const annee = (filtresRecap.date_debut || today()).slice(0, 4);
      const periodeLabel = vueRecap === 'annee'
        ? `Production annuelle ${annee}`
        : `Du ${filtresRecap.date_debut} au ${filtresRecap.date_fin}`;
      const regionLabel = filtresRecap.region ? ` — Région : <strong>${filtresRecap.region}</strong>` : ' — Toutes régions';
      const recapRows = lignesRegions.map(l => `<tr>
        <td>${l.region}</td>
        <td class="r blue">${fmt(l.nbre_bovins)}</td><td class="r">${fmtP(l.poids_bovins)} kg</td>
        <td class="r purple">${fmt(l.nbre_ovins)}</td><td class="r">${fmtP(l.poids_ovins)} kg</td>
        <td class="r orange">${fmt(l.nbre_caprins)}</td><td class="r">${fmtP(l.poids_caprins)} kg</td>
        <td class="r pink">${fmt(l.nbre_porcins)}</td><td class="r">${fmtP(l.poids_porcins)} kg</td>
        <td class="r">${fmt(l.nbre_camelins)}</td><td class="r">${fmtP(l.poids_camelins)} kg</td>
        <td class="r red">${fmtP(l.viande_foraine)}</td>
        <td class="r green b">${fmtP(l.poids_total)} kg</td>
      </tr>`).join('');
      const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>Récapitulatif par région — SOGAS</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:9px;color:#222}
      .hdr{background:#1a5c2a;color:#fff;padding:12px 20px;display:flex;justify-content:space-between;align-items:center}
      .hdr h1{font-size:15px;font-weight:900}.hdr .m{font-size:9px;text-align:right;opacity:.9;line-height:1.7}
      .body{padding:16px 20px}.pbox{background:#f5f5f5;border-left:4px solid #1a5c2a;padding:7px 14px;margin:10px 0;font-size:10px}
      table{width:100%;border-collapse:collapse;margin-top:8px}
      th{background:#1B5E20;color:#fff;padding:4px 5px;text-align:center;font-size:8px;white-space:nowrap}
      td{padding:3px 5px;border-bottom:1px solid #f0f0f0;color:#212121;white-space:nowrap}
      tr:nth-child(even) td{background:#E8F5E9}.r{text-align:right}.b{font-weight:700}
      .tot td{background:#2E7D32;color:#fff;font-weight:800}
      .foot{margin-top:14px;padding:7px;border-top:1px solid #e5e7eb;font-size:8px;color:#6b7280;text-align:center}
      @page{size:A4 landscape;margin:.8cm}@media print{body{font-size:8px}}
      </style></head><body>
      <div class="hdr"><div><div style="font-size:18px;font-weight:900;letter-spacing:2px">SOGAS</div><h1>Récapitulatif de Production par Région</h1></div>
      <div class="m">Téléchargé par : <strong>${nom}</strong><br>Date : <strong>${dateStr}</strong> à <strong>${heureStr}</strong><br>${periodeLabel}</div></div>
      <div class="body">
      <div class="pbox">Rapport généré le <strong>${dateStr} à ${heureStr}</strong> — ${periodeLabel}${regionLabel}</div>
      <table><thead>
        <tr>
          <th rowspan="2" style="background:#1B5E20">RÉGIONS</th>
          <th colspan="2" style="background:#1B5E20">Bovins</th>
          <th colspan="2" style="background:#1B5E20">Ovins</th>
          <th colspan="2" style="background:#1B5E20">Caprins</th>
          <th colspan="2" style="background:#1B5E20">Porcins</th>
          <th colspan="2" style="background:#1B5E20">Camelins</th>
          <th rowspan="2" style="background:#1B5E20">V.F</th>
          <th rowspan="2" style="background:#1B5E20">PTVT kg</th>
        </tr>
        <tr>
          <th style="background:#1B5E20">Nbre</th><th style="background:#1B5E20">Poids</th>
          <th style="background:#1B5E20">Nbre</th><th style="background:#1B5E20">Poids</th>
          <th style="background:#1B5E20">Nbre</th><th style="background:#1B5E20">Poids</th>
          <th style="background:#1B5E20">Nbre</th><th style="background:#1B5E20">Poids</th>
          <th style="background:#1B5E20">Nbre</th><th style="background:#1B5E20">Poids</th>
        </tr>
      </thead><tbody>
        ${recapRows}
        <tr class="tot">
          <td class="b">TOTAL</td>
          <td class="r b">${fmt(totRegions.nbre_bovins)}</td><td class="r b">${fmtP(totRegions.poids_bovins)} kg</td>
          <td class="r b">${fmt(totRegions.nbre_ovins)}</td><td class="r b">${fmtP(totRegions.poids_ovins)} kg</td>
          <td class="r b">${fmt(totRegions.nbre_caprins)}</td><td class="r b">${fmtP(totRegions.poids_caprins)} kg</td>
          <td class="r b">${fmt(totRegions.nbre_porcins)}</td><td class="r b">${fmtP(totRegions.poids_porcins)} kg</td>
          <td class="r b">${fmt(totRegions.nbre_camelins)}</td><td class="r b">${fmtP(totRegions.poids_camelins)} kg</td>
          <td class="r b">${fmtP(totRegions.viande_foraine)}</td>
          <td class="r b">${fmtP(totRegions.poids_total)} kg</td>
        </tr>
      </tbody></table>
      <div class="foot">SOGAS — Société de Gestion des Abattoirs du Sénégal — Document généré automatiquement le ${dateStr} à ${heureStr}</div>
      </div><script>window.onload=function(){window.print()}</script></body></html>`;
      const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      return;
    }

    if (!donnees) return;
    const titre = onglet === 'ca' ? "Rapport Chiffre d'Affaires" : onglet === 'production' ? 'Rapport de Production' : 'Rapport de Caisse';
    const lignes = donnees.lignes || [];
    const t = donnees.totaux || {};
    const tBovPdf = (+t.bovins_stab_jour||0)+(+t.bovins_stab_nuit||0)+(+t.bovins_abattage||0)+(+t.bovins_ressuage||0);

    let head = '', rows = '', totauxHtml = '';

    if (onglet === 'ca') {
      head = '<tr><th>Période</th><th>Région</th><th>Abattoir</th><th>Type</th><th>Stab J.</th><th>Stab N.</th><th>Abatt.Bov</th><th>Ressuage</th><th>S/T Bovins</th><th>S/T Ovins</th><th>S/T Caprins</th><th>S/T Porcins</th><th>VF</th><th>Frigo</th><th>Location</th><th>Timbres</th><th>Tickets</th><th>TOTAL CA</th></tr>';
      rows = lignes.map(l => `<tr><td>${l.periode}</td><td>${l.region}</td><td>${l.abattoir_nom}</td><td>${l.type_jour||'STD'}</td><td class="r">${fmtF(l.bovins_stab_jour)}</td><td class="r">${fmtF(l.bovins_stab_nuit)}</td><td class="r">${fmtF(l.bovins_abattage)}</td><td class="r">${fmtF(l.bovins_ressuage)}</td><td class="r b">${fmtF(l.total_bovins)}</td><td class="r b">${fmtF(l.total_ovins)}</td><td class="r b">${fmtF(l.total_caprins)}</td><td class="r b">${fmtF(l.total_porcins)}</td><td class="r red">${fmtF(l.vf_montant)}</td><td class="r">${fmtF(l.stockage_frigo)}</td><td class="r">${fmtF(l.location)}</td><td class="r">${fmtF(l.timbres)}</td><td class="r">${fmtF(l.tickets)}</td><td class="r green b">${fmtF(l.total_ca)}</td></tr>`).join('');
      totauxHtml = `<div class="cards"><div class="card"><div class="cl">TOTAL CA</div><div class="cv green">${fmtF(t.total_ca)}</div></div><div class="card"><div class="cl">Bovins</div><div class="cv blue">${fmtF(tBovPdf)}</div></div><div class="card"><div class="cl">Ovins</div><div class="cv purple">${fmtF(t.ovins_total)}</div></div><div class="card"><div class="cl">Caprins</div><div class="cv orange">${fmtF(t.caprins_total)}</div></div><div class="card"><div class="cl">Porcins</div><div class="cv pink">${fmtF(t.porcins_total)}</div></div><div class="card"><div class="cl">Viande Foraine</div><div class="cv red">${fmtF(t.vf_montant)}</div></div></div>`;
    } else if (onglet === 'production') {
      head = '<tr><th>Date</th><th>Région</th><th>Abattoir</th><th>Bov.N</th><th>Pds Bov</th><th>Camelins</th><th>Pds Cam</th><th>Ovins</th><th>Pds Ov</th><th>Caprins</th><th>Pds Cap</th><th>Porcins</th><th>Pds Por</th><th>Total Têtes</th><th>Poids Total</th></tr>';
      rows = lignes.map(l => { const pt = (+l.poids_bovins||0)+(+l.poids_camelins||0)+(+l.poids_ovins||0)+(+l.poids_caprins||0)+(+l.poids_porcins||0); return `<tr><td>${fmtDate(l.date_saisie)}</td><td>${l.region}</td><td>${l.abattoir_nom}</td><td class="r blue">${fmt(l.bovins_nets)}</td><td class="r">${fmt(l.poids_bovins)} kg</td><td class="r">${fmt(l.camelins)}</td><td class="r">${fmt(l.poids_camelins||0)} kg</td><td class="r purple">${fmt(l.ovins)}</td><td class="r">${fmt(l.poids_ovins)} kg</td><td class="r orange">${fmt(l.caprins)}</td><td class="r">${fmt(l.poids_caprins)} kg</td><td class="r pink">${fmt(l.porcins)}</td><td class="r">${fmt(l.poids_porcins||0)} kg</td><td class="r green b">${fmt(l.total_animaux)}</td><td class="r green b">${fmt(pt.toFixed(1))} kg</td></tr>`; }).join('');
      totauxHtml = `<div class="cards"><div class="card"><div class="cl">Bovins Nets</div><div class="cv blue">${fmt(t.bovins_nets)} têtes</div></div><div class="card"><div class="cl">Ovins</div><div class="cv purple">${fmt(t.ovins)} têtes</div></div><div class="card"><div class="cl">Caprins</div><div class="cv orange">${fmt(t.caprins)} têtes</div></div><div class="card"><div class="cl">Total Animaux</div><div class="cv green">${fmt(t.total_animaux)} têtes</div></div></div>`;
    } else {
      head = '<tr><th>Date</th><th>Région</th><th>Abattoir</th><th>Caissier</th><th>Encaissé</th><th>Décaissé</th><th>Solde Net</th><th>Statut</th></tr>';
      rows = lignes.map(l => `<tr><td>${fmtDate(l.date_rapport)}</td><td>${l.region}</td><td>${l.abattoir_nom}</td><td>${l.caissier_nom}</td><td class="r green">${fmtF(l.total_encaisse)}</td><td class="r red">${fmtF(l.total_decaisse)}</td><td class="r b">${fmtF(l.solde_net)}</td><td>${l.statut}</td></tr>`).join('');
      totauxHtml = `<div class="cards"><div class="card"><div class="cl">Total Encaissé</div><div class="cv green">${fmtF(t.total_encaisse)}</div></div><div class="card"><div class="cl">Total Décaissé</div><div class="cv red">${fmtF(t.total_decaisse)}</div></div><div class="card"><div class="cl">Solde Net</div><div class="cv green">${fmtF(t.solde_net)}</div></div></div>`;
    }

    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${titre} — SOGAS</title>
    <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:9px;color:#222}
    .hdr{background:#1a5c2a;color:#fff;padding:12px 20px;display:flex;justify-content:space-between;align-items:center}
    .hdr h1{font-size:15px;font-weight:900}.hdr .m{font-size:9px;text-align:right;opacity:.9;line-height:1.7}
    .body{padding:16px 20px}.pbox{background:#f5f5f5;border-left:4px solid #1a5c2a;padding:7px 14px;margin:10px 0;font-size:10px}
    .cards{display:flex;flex-wrap:wrap;gap:8px;margin:10px 0}
    .card{background:#f9fafb;border:1px solid #e5e7eb;border-radius:5px;padding:7px 12px;min-width:130px}
    .cl{font-size:8px;color:#6b7280;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px}
    .cv{font-size:13px;font-weight:800}
    .green{color:#059669}.blue{color:#1d4ed8}.purple{color:#7c3aed}.orange{color:#d97706}.red{color:#dc2626}.pink{color:#db2777}
    table{width:100%;border-collapse:collapse;margin-top:8px}
    th{background:#1a5c2a;color:#fff;padding:4px 5px;text-align:left;font-size:8px;white-space:nowrap}
    td{padding:3px 5px;border-bottom:1px solid #f0f0f0;white-space:nowrap}
    tr:nth-child(even) td{background:#fafafa}.r{text-align:right}.b{font-weight:700}
    .foot{margin-top:14px;padding:7px;border-top:1px solid #e5e7eb;font-size:8px;color:#6b7280;text-align:center}
    @page{size:A4 landscape;margin:.8cm}@media print{body{font-size:8px}}
    </style></head><body>
    <div class="hdr"><div><div style="font-size:18px;font-weight:900;letter-spacing:2px">SOGAS</div><h1>${titre}</h1></div>
    <div class="m">Téléchargé par : <strong>${nom}</strong><br>Date : <strong>${dateStr}</strong> à <strong>${heureStr}</strong><br>Période : ${filtres.date_debut} → ${filtres.date_fin}</div></div>
    <div class="body">
    <div class="pbox">Rapport généré le <strong>${dateStr} à ${heureStr}</strong> — Période du <strong>${filtres.date_debut}</strong> au <strong>${filtres.date_fin}</strong>${filtres.region ? ' — Région : <strong>' + filtres.region + '</strong>' : ' — Toutes régions'}</div>
    ${totauxHtml}
    <table><thead>${head}</thead><tbody>${rows}</tbody></table>
    <div class="foot">SOGAS — Société de Gestion des Abattoirs du Sénégal — Document généré automatiquement le ${dateStr} à ${heureStr}</div>
    </div><script>window.onload=function(){window.print()}</script></body></html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  };

  const exportExcel = () => {
    if (onglet === 'recap') {
      if (!lignesRegions.length) return;
      const annee = (filtresRecap.date_debut || today()).slice(0, 4);
      const dDebut = vueRecap === 'annee' ? `${annee}-01-01` : filtresRecap.date_debut;
      const dFin   = vueRecap === 'annee' ? `${annee}-12-31` : filtresRecap.date_fin;
      let csv = 'Région;Bov.Nbre;Bov.Poids;Ov.Nbre;Ov.Poids;Cap.Nbre;Cap.Poids;Por.Nbre;Por.Poids;Cam.Nbre;Cam.Poids;V.F;PTVT kg\n';
      lignesRegions.forEach(l => { csv += `${l.region};${l.nbre_bovins};${Number(l.poids_bovins).toFixed(2)};${l.nbre_ovins};${Number(l.poids_ovins).toFixed(2)};${l.nbre_caprins};${Number(l.poids_caprins).toFixed(2)};${l.nbre_porcins};${Number(l.poids_porcins).toFixed(2)};${l.nbre_camelins};${Number(l.poids_camelins).toFixed(2)};${Number(l.viande_foraine).toFixed(2)};${Number(l.poids_total).toFixed(2)}\n`; });
      csv += `TOTAL;${totRegions.nbre_bovins};${Number(totRegions.poids_bovins).toFixed(2)};${totRegions.nbre_ovins};${Number(totRegions.poids_ovins).toFixed(2)};${totRegions.nbre_caprins};${Number(totRegions.poids_caprins).toFixed(2)};${totRegions.nbre_porcins};${Number(totRegions.poids_porcins).toFixed(2)};${totRegions.nbre_camelins};${Number(totRegions.poids_camelins).toFixed(2)};${Number(totRegions.viande_foraine).toFixed(2)};${Number(totRegions.poids_total).toFixed(2)}\n`;
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `recap_regions_${dDebut}_${dFin}.csv`; a.click();
      return;
    }

    if (!donnees?.lignes?.length) return;
    const lignes = donnees.lignes;
    let csv = '';
    if (onglet === 'ca') {
      csv = 'Période;Région;Abattoir;Type;Stab J.;Stab N.;Abatt.;Ressuage;S/T Bovins;S/T Ovins;S/T Caprins;S/T Porcins;VF;Frigo;Stockage;Location;Timbres;Tickets;TOTAL CA\n';
      lignes.forEach(l => { csv += `${l.periode};${l.region};${l.abattoir_nom};${l.type_jour||'STD'};${l.bovins_stab_jour};${l.bovins_stab_nuit};${l.bovins_abattage};${l.bovins_ressuage};${l.total_bovins};${l.total_ovins};${l.total_caprins};${l.total_porcins};${l.vf_montant};${l.stockage_frigo};${l.stockage_abattoir};${l.location};${l.timbres};${l.tickets};${l.total_ca}\n`; });
    } else if (onglet === 'production') {
      csv = 'Date;Région;Abattoir;Bov.Nets;Pds Bov;Camelins;Pds Cam;Ovins;Pds Ov;Caprins;Pds Cap;Porcins;Pds Por;Total Têtes;Poids Total\n';
      lignes.forEach(l => { const pt = (+l.poids_bovins||0)+(+l.poids_camelins||0)+(+l.poids_ovins||0)+(+l.poids_caprins||0)+(+l.poids_porcins||0); csv += `${l.date_saisie};${l.region};${l.abattoir_nom};${l.bovins_nets};${l.poids_bovins};${l.camelins};${l.poids_camelins||0};${l.ovins};${l.poids_ovins};${l.caprins};${l.poids_caprins};${l.porcins};${l.poids_porcins||0};${l.total_animaux};${pt.toFixed(1)}\n`; });
    } else {
      csv = 'Date;Région;Abattoir;Caissier;Encaissé;Décaissé;Solde Net;Statut\n';
      lignes.forEach(l => { csv += `${l.date_rapport};${l.region};${l.abattoir_nom};${l.caissier_nom};${l.total_encaisse};${l.total_decaisse};${l.solde_net};${l.statut}\n`; });
    }
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `rapport_${onglet}_${filtres.date_debut}_${filtres.date_fin}.csv`; a.click();
  };

  const lignes = donnees?.lignes || [];
  const totaux = donnees?.totaux;
  const totalPages = Math.ceil(lignes.length / PAGE_SIZE);
  const lignePage = lignes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const tBov = totaux ? (+totaux.bovins_stab_jour||0)+(+totaux.bovins_stab_nuit||0)+(+totaux.bovins_abattage||0)+(+totaux.bovins_ressuage||0) : 0;
  const poidsTotal = totaux ? (+totaux.poids_bovins||0)+(+totaux.poids_ovins||0)+(+totaux.poids_caprins||0)+(+totaux.poids_porcins||0) : 0;
  const lignesRegions = donneesRegions?.lignes || [];
  const totRegions = lignesRegions.reduce((acc, l) => ({
    nbre_bovins:    acc.nbre_bovins    + (+l.nbre_bovins||0),
    poids_bovins:   acc.poids_bovins   + (+l.poids_bovins||0),
    nbre_ovins:     acc.nbre_ovins     + (+l.nbre_ovins||0),
    poids_ovins:    acc.poids_ovins    + (+l.poids_ovins||0),
    nbre_caprins:   acc.nbre_caprins   + (+l.nbre_caprins||0),
    poids_caprins:  acc.poids_caprins  + (+l.poids_caprins||0),
    nbre_porcins:   acc.nbre_porcins   + (+l.nbre_porcins||0),
    poids_porcins:  acc.poids_porcins  + (+l.poids_porcins||0),
    nbre_camelins:  acc.nbre_camelins  + (+l.nbre_camelins||0),
    poids_camelins: acc.poids_camelins + (+l.poids_camelins||0),
    viande_foraine: acc.viande_foraine + (+l.viande_foraine||0),
    poids_total:    acc.poids_total    + (+l.poids_total||0),
  }), {nbre_bovins:0,poids_bovins:0,nbre_ovins:0,poids_ovins:0,nbre_caprins:0,poids_caprins:0,nbre_porcins:0,poids_porcins:0,nbre_camelins:0,poids_camelins:0,viande_foraine:0,poids_total:0});

  const S = {
    page: { minHeight:'100vh', background: C.light },
    topbar: { height:52, background:'#fff', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', position:'sticky', top:0, zIndex:100, flexShrink:0 },
    breadcrumb: { fontSize:13, color: C.textLight, display:'flex', alignItems:'center', gap:6 },
    breadcrumbActive: { color: C.textDark, fontWeight:600 },
    body: { maxWidth:1400, margin:'20px auto', padding:'0 20px', paddingBottom:40 },
    onglets: { display:'flex', gap:3, background:'#fff', padding:5, borderRadius:10, border:`1px solid ${C.border}`, marginBottom:20, width:'fit-content' },
    oBtn: (a) => ({ padding:'8px 22px', borderRadius:7, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background: a ? C.primary : 'transparent', color: a ? '#fff' : C.textMid }),
    filtBox: { background:'#fff', borderRadius:10, padding:'14px 18px', marginBottom:18, border:`0.5px solid ${C.border}`, boxShadow:'0 1px 3px rgba(0,0,0,.05)' },
    filtRow: { display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end' },
    fg: { display:'flex', flexDirection:'column', gap:4 },
    lbl: { fontSize:11, fontWeight:600, color: C.textMid, textTransform:'uppercase', letterSpacing:.4 },
    inp: { padding:'7px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', color: C.textDark },
    btnG: (c) => ({ padding:'7px 18px', background:c||C.primary, color:'#fff', border:'none', borderRadius:7, cursor:'pointer', fontWeight:600, fontSize:13 }),
    cards: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:18 },
    card: () => ({ background:'#fff', borderRadius:10, padding:'14px 16px', border:`0.5px solid ${C.border}`, boxShadow:'0 1px 3px rgba(0,0,0,.06)', display:'flex', alignItems:'center', gap:12 }),
    cIco: (bg) => ({ width:40, height:40, background:bg, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }),
    cLbl: { fontSize:10, color: C.textLight, fontWeight:600, textTransform:'uppercase', letterSpacing:.4 },
    cVal: (c) => ({ fontSize:18, fontWeight:800, color:c, marginTop:2 }),
    tBox: { background:'#fff', borderRadius:12, border:`0.5px solid ${C.border}`, boxShadow:'0 1px 3px rgba(0,0,0,.06)', overflow:'hidden' },
    tHdr: { padding:'12px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:'#FAFAFA' },
    tTit: { fontSize:13, fontWeight:700, color: C.textDark },
    th: (a,bg,c) => ({ padding:'8px 10px', fontSize:10, fontWeight:700, color:c||C.textMid, textTransform:'uppercase', letterSpacing:.5, background:bg||'#F8F9FA', borderBottom:`1px solid ${C.border}`, textAlign:a||'left', whiteSpace:'nowrap' }),
    td: (a,c,bg,fw) => ({ padding:'9px 10px', fontSize:12, color:c||C.textDark, borderBottom:'1px solid #F0F2F4', whiteSpace:'nowrap', textAlign:a||'left', fontWeight:fw||400, background:bg||'transparent' }),
    pag: { padding:'10px 16px', borderTop:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:'#FAFAFA' },
    pagBtn: (a) => ({ padding:'5px 11px', border:`1px solid ${a?C.primary:C.border}`, borderRadius:6, background:a?C.primary:'#fff', color:a?'#fff':C.textDark, cursor:'pointer', fontSize:12, fontWeight:a?700:400 }),
    vide: { padding:48, textAlign:'center', color: C.textLight, fontSize:14 },
    badge: (c,bg) => ({ display:'inline-block', background:bg, color:c, padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700 }),
  };

  const Pagination = () => totalPages > 1 ? (
    <div style={S.pag}>
      <span style={{ fontSize:13, color:'#6b7280' }}>Page <strong>{page}</strong> / <strong>{totalPages}</strong> — {lignes.length} ligne(s)</span>
      <div style={{ display:'flex', gap:4 }}>
        {[['«',()=>setPage(1),page===1],['‹',()=>setPage(p=>Math.max(1,p-1)),page===1]].map(([l,f,d],i)=>(
          <button key={i} onClick={f} disabled={d} style={{...S.pagBtn(false), color:d?'#d1d5db':'#374151', cursor:d?'default':'pointer'}}>{l}</button>
        ))}
        {Array.from({length:Math.min(5,totalPages)},(_,i)=>{const p=page<=3?i+1:page-2+i;return p>totalPages?null:<button key={p} onClick={()=>setPage(p)} style={S.pagBtn(p===page)}>{p}</button>;})}
        {[['›',()=>setPage(p=>Math.min(totalPages,p+1)),page===totalPages],['»',()=>setPage(totalPages),page===totalPages]].map(([l,f,d],i)=>(
          <button key={i} onClick={f} disabled={d} style={{...S.pagBtn(false), color:d?'#d1d5db':'#374151', cursor:d?'default':'pointer'}}>{l}</button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div style={S.page}>
      {/* Topbar */}
      <div style={S.topbar}>
        <div style={S.breadcrumb}>
          <span>Rapports</span>
          <span style={{ color: C.border }}>›</span>
          <span style={S.breadcrumbActive}>Statistiques</span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={exportPDF} style={S.btnG('#6A1B9A')}>🖨️ PDF</button>
          <button onClick={exportExcel} style={S.btnG('#1565C0')}>📥 Excel</button>
        </div>
      </div>

      <div style={S.body}>

        <div style={S.onglets}>
          {[['ca','📊 Chiffre d\'Affaires'],['production','📊 Production'],['recap','📋 Récapitulatif'],['caisse','💰 Caisse']].filter(([k])=>!(isPreposeDakar && k==='caisse')).map(([k,l])=>(
            <button key={k} style={S.oBtn(onglet===k)} onClick={()=>{setOnglet(k);setPage(1);}}>{l}</button>
          ))}
        </div>

        {/* Filtres CA / Production / Caisse */}
        {onglet !== 'recap' && (
          <div style={S.filtBox}>
            <div style={S.filtRow}>
              <div style={S.fg}><span style={S.lbl}>Date début</span><input type="date" style={S.inp} value={filtres.date_debut} onChange={e=>setFiltres(f=>({...f,date_debut:e.target.value}))}/></div>
              <div style={S.fg}><span style={S.lbl}>Date fin</span><input type="date" style={S.inp} value={filtres.date_fin} onChange={e=>setFiltres(f=>({...f,date_fin:e.target.value}))}/></div>
              {onglet==='ca' && <div style={S.fg}><span style={S.lbl}>Période</span>
                <select style={S.inp} value={filtres.periode} onChange={e=>setFiltres(f=>({...f,periode:e.target.value}))}>
                  <option value="jour">Par jour</option><option value="mois">Par mois</option><option value="annee">Par année</option>
                </select></div>}
              <div style={S.fg}><span style={S.lbl}>Région</span>
                {isPreposeLimite ? (
                  <div style={{...S.inp, background:'#f3f4f6', color:'#374151', fontWeight:600}}>
                    📍 {regionUser}
                  </div>
                ) : (
                  <select style={{...S.inp,minWidth:140}} value={filtres.region} onChange={e=>setFiltres(f=>({...f,region:e.target.value,abattoir_id:''}))}>
                    <option value="">Toutes les régions</option>{regions.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                )}
              </div>
              <div style={S.fg}><span style={S.lbl}>Abattoir</span>
                <select style={{...S.inp,minWidth:160}} value={filtres.abattoir_id} onChange={e=>setFiltres(f=>({...f,abattoir_id:e.target.value}))}>
                  <option value="">Tous</option>{abattoirs.filter(a => isPreposeLimite ? a.region===regionUser : (!filtres.region||a.region===filtres.region)).map(a=><option key={a.id} value={a.id}>{a.nom}</option>)}
                </select></div>
              <button onClick={charger} style={S.btnG()} disabled={chargement}>{chargement?'⏳ Chargement...':'🔍 Générer'}</button>
              <button onClick={()=>setFiltres({date_debut:debutMois(),date_fin:today(),periode:'jour',region:'',abattoir_id:''})} style={S.btnG('#6b7280')}>✖ Réinitialiser</button>
            </div>
          </div>
        )}

        {/* Filtres Récapitulatif */}
        {onglet === 'recap' && (
          <div style={S.filtBox}>
            <div style={S.filtRow}>
              <div style={S.fg}><span style={S.lbl}>Date début</span><input type="date" style={S.inp} value={filtresRecap.date_debut} onChange={e=>setFiltresRecap(f=>({...f,date_debut:e.target.value}))}/></div>
              <div style={S.fg}><span style={S.lbl}>Date fin</span><input type="date" style={S.inp} value={filtresRecap.date_fin} onChange={e=>setFiltresRecap(f=>({...f,date_fin:e.target.value}))}/></div>
              <div style={S.fg}><span style={S.lbl}>Vue</span>
                <div style={{display:'flex',gap:2,background:'#F4F6F9',borderRadius:8,padding:3}}>
                  {[['mois','Par mois'],['annee','Par année']].map(([k,l])=>(
                    <button key={k} onClick={()=>setVueRecap(k)}
                      style={{padding:'6px 14px',borderRadius:6,border:'none',cursor:'pointer',fontSize:12,fontWeight:600,
                              background:vueRecap===k?C.primary:'transparent',color:vueRecap===k?'#fff':C.textMid}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div style={S.fg}><span style={S.lbl}>Région</span>
                {isPreposeLimite ? (
                  <div style={{...S.inp, background:'#f3f4f6', color:'#374151', fontWeight:600}}>
                    📍 {regionUser}
                  </div>
                ) : (
                  <select style={{...S.inp,minWidth:140}} value={filtresRecap.region} onChange={e=>setFiltresRecap(f=>({...f,region:e.target.value}))}>
                    <option value="">Toutes les régions</option>{regions.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                )}
              </div>
              <button onClick={chargerRecap} style={S.btnG()} disabled={chargement}>{chargement?'⏳ Chargement...':'🔍 Générer'}</button>
              <button onClick={()=>{setFiltresRecap({date_debut:debutMois(),date_fin:today(),region:isPreposeLimite?regionUser:''});setDonneesRegions(null);}} style={S.btnG('#6b7280')}>✖ Réinitialiser</button>
            </div>
          </div>
        )}

        {/* Bandeau rôle */}
        {(isPreposeLimite || isPreposeDakar) && (
          <div style={{ background: isPreposeDakar?'#eff6ff':'#f0fdf4', border:`1px solid ${isPreposeDakar?'#bfdbfe':'#bbf7d0'}`, borderRadius:8, padding:'10px 16px', marginBottom:16, display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
            <span style={{ fontSize:18 }}>{isPreposeDakar ? '👁️' : '📍'}</span>
            <span style={{ color: isPreposeDakar?'#1d4ed8':'#166534', fontWeight:600 }}>
              {isPreposeDakar
                  ? `Préposé Dakar – Accès à tous les rapports de production et chiffres d'affaires`
                : `Préposé ${regionUser} — Accès limité à votre région uniquement`}
            </span>
          </div>
        )}

        {chargement && <div style={{...S.tBox,...S.vide}}><div style={{fontSize:32,marginBottom:8}}>⏳</div>Génération en cours...</div>}

        {!chargement && donnees && onglet !== 'recap' && (
          <>
            {/* CARTES — CA et Caisse uniquement */}
            {(onglet==='ca' || onglet==='caisse') && (
              <div style={S.cards}>
                {onglet==='ca' && [
                  {icon:'💹',label:'Total CA',val:fmtF(totaux?.total_ca),color:'#059669',bg:'#f0fdf4'},
                  {icon:'🐄',label:'Total Bovins',val:fmtF(tBov),color:'#1d4ed8',bg:'#eff6ff'},
                  {icon:'🐑',label:'Total Ovins',val:fmtF(totaux?.ovins_total),color:'#7c3aed',bg:'#faf5ff'},
                  {icon:'🐐',label:'Total Caprins',val:fmtF(totaux?.caprins_total),color:'#d97706',bg:'#fffbeb'},
                  {icon:'🐷',label:'Total Porcins',val:fmtF(totaux?.porcins_total),color:'#db2777',bg:'#fdf2f8'},
                  {icon:'🥩',label:'Viande Foraine',val:fmtF(totaux?.vf_montant),color:'#dc2626',bg:'#fef2f2'},
                ].map((c,i)=>(
                  <div key={i} style={S.card()}>
                    <div style={S.cIco(c.bg)}>{c.icon}</div>
                    <div><div style={S.cLbl}>{c.label}</div><div style={S.cVal(c.color)}>{c.val}</div></div>
                  </div>
                ))}
                {onglet==='caisse' && [
                  {icon:'💵',label:'Total Encaissé',val:fmtF(totaux?.total_encaisse),color:'#059669',bg:'#f0fdf4'},
                  {icon:'💸',label:'Total Décaissé',val:fmtF(totaux?.total_decaisse),color:'#dc2626',bg:'#fef2f2'},
                  {icon:'💰',label:'Solde Net',val:fmtF(totaux?.solde_net),color:(totaux?.solde_net>=0?'#059669':'#dc2626'),bg:(totaux?.solde_net>=0?'#f0fdf4':'#fef2f2')},
                  {icon:'📋',label:'Rapports',val:fmt(totaux?.nb_rapports),color:'#374151',bg:'#f9fafb'},
                  {icon:'✅',label:'Validés',val:fmt(totaux?.nb_valides),color:'#059669',bg:'#f0fdf4'},
                ].map((c,i)=>(
                  <div key={i} style={S.card()}>
                    <div style={S.cIco(c.bg)}>{c.icon}</div>
                    <div><div style={S.cLbl}>{c.label}</div><div style={S.cVal(c.color)}>{c.val}</div></div>
                  </div>
                ))}
              </div>
            )}

            {/* TABLE CA */}
            {onglet==='ca' && (
              <div style={S.tBox}>
                <div style={S.tHdr}>
                  <span style={S.tTit}>💹 Chiffre d'Affaires — {lignes.length} ligne(s)</span>
                  <span style={{fontSize:12,color:'#9ca3af'}}>Du {fmtDate(donnees.date_debut)} au {fmtDate(donnees.date_fin)}</span>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr>
                      <th style={S.th('left')}>Période</th><th style={S.th('left')}>Région</th><th style={S.th('left')}>Abattoir</th><th style={S.th('left')}>Type</th>
                      <th style={S.th('right','#eff6ff','#1d4ed8')}>Stab Jour</th><th style={S.th('right','#eff6ff','#1d4ed8')}>Stab Nuit</th>
                      <th style={S.th('right','#eff6ff','#1d4ed8')}>Abatt.Bov</th><th style={S.th('right','#eff6ff','#1d4ed8')}>Ressuage</th>
                      <th style={S.th('right','#dbeafe','#1e40af')}>S/T Bovins</th>
                      <th style={S.th('right','#ede9fe','#5b21b6')}>S/T Ovins</th>
                      <th style={S.th('right','#fef9c3','#78350f')}>S/T Caprins</th>
                      <th style={S.th('right','#ffedd5','#9a3412')}>S/T Porcins</th>
                      <th style={S.th('right','#fef2f2','#dc2626')}>Viande For.</th>
                      <th style={S.th('right')}>Frigo</th><th style={S.th('right')}>Stockage</th><th style={S.th('right')}>Location</th><th style={S.th('right')}>Timbres</th><th style={S.th('right')}>Tickets</th>
                      <th style={S.th('right','#f0fdf4','#166534')}>TOTAL CA</th>
                    </tr></thead>
                    <tbody>
                      {lignePage.length===0
                        ? <tr><td colSpan={19} style={{...S.td(),textAlign:'center',padding:40,color:'#9ca3af'}}>📭 Aucune donnée pour cette période</td></tr>
                        : lignePage.map((l,i)=>(
                          <tr key={i} style={{background:i%2===0?'#fff':'#fafafa'}}>
                            <td style={S.td('left',VERT,null,600)}>{l.periode}</td>
                            <td style={S.td()}><span style={S.badge('#0369a1','#dbeafe')}>{l.region}</span></td>
                            <td style={S.td()}>{l.abattoir_nom}</td>
                            <td style={S.td()}><span style={S.badge(l.type_jour==='SPECIAL'?'#92400e':'#0369a1',l.type_jour==='SPECIAL'?'#fef3c7':'#e0f2fe')}>{l.type_jour||'STD'}</span></td>
                            <td style={S.td('right')}>{fmtF(l.bovins_stab_jour)}</td>
                            <td style={S.td('right','#7c3aed',null,600)}>{fmtF(l.bovins_stab_nuit)}</td>
                            <td style={S.td('right')}>{fmtF(l.bovins_abattage)}</td>
                            <td style={S.td('right')}>{fmtF(l.bovins_ressuage)}</td>
                            <td style={S.td('right','#1e40af','#f0f9ff',700)}>{fmtF(l.total_bovins)}</td>
                            <td style={S.td('right','#5b21b6','#f5f3ff',700)}>{fmtF(l.total_ovins)}</td>
                            <td style={S.td('right','#78350f','#fffbeb',700)}>{fmtF(l.total_caprins)}</td>
                            <td style={S.td('right','#9a3412','#fff7ed',700)}>{fmtF(l.total_porcins)}</td>
                            <td style={S.td('right','#dc2626',null,700)}>{fmtF(l.vf_montant)}</td>
                            <td style={S.td('right')}>{fmtF(l.stockage_frigo)}</td>
                            <td style={S.td('right')}>{fmtF(l.stockage_abattoir)}</td>
                            <td style={S.td('right')}>{fmtF(l.location)}</td>
                            <td style={S.td('right')}>{fmtF(l.timbres)}</td>
                            <td style={S.td('right')}>{fmtF(l.tickets)}</td>
                            <td style={S.td('right',VERT,'#f0fdf4',800)}>{fmtF(l.total_ca)}</td>
                          </tr>
                        ))
                      }
                      {totaux && lignes.length>0 && (
                        <tr style={{background:'#f0fdf4',borderTop:'2px solid #bbf7d0'}}>
                          <td colSpan={4} style={S.td('left',VERT,null,800)}>TOTAL GÉNÉRAL</td>
                          <td style={S.td('right',null,null,700)}>{fmtF(totaux.bovins_stab_jour)}</td>
                          <td style={S.td('right',null,null,700)}>{fmtF(totaux.bovins_stab_nuit)}</td>
                          <td style={S.td('right',null,null,700)}>{fmtF(totaux.bovins_abattage)}</td>
                          <td style={S.td('right',null,null,700)}>{fmtF(totaux.bovins_ressuage)}</td>
                          <td style={S.td('right','#1e40af',null,800)}>{fmtF(tBov)}</td>
                          <td style={S.td('right','#5b21b6',null,800)}>{fmtF(totaux.ovins_total)}</td>
                          <td style={S.td('right','#78350f',null,800)}>{fmtF(totaux.caprins_total)}</td>
                          <td style={S.td('right','#9a3412',null,800)}>{fmtF(totaux.porcins_total)}</td>
                          <td style={S.td('right','#dc2626',null,800)}>{fmtF(totaux.vf_montant)}</td>
                          <td colSpan={5} style={S.td()}/>
                          <td style={S.td('right',VERT,'#dcfce7',800)}>{fmtF(totaux.total_ca)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination/>
              </div>
            )}

            {/* TABLE PRODUCTION */}
            {onglet==='production' && (
              <div style={S.tBox}>
                <div style={S.tHdr}>
                  <span style={S.tTit}>🏭 Rapport de Production — {lignes.length} ligne(s)</span>
                  <span style={{fontSize:12,color:'#9ca3af'}}>Du {fmtDate(donnees.date_debut)} au {fmtDate(donnees.date_fin)}</span>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr>
                      <th style={S.th('left')}>Date</th><th style={S.th('left')}>Région</th><th style={S.th('left')}>Abattoir</th>
                      <th style={S.th('right','#eff6ff','#1d4ed8')}>Bov. Nets</th><th style={S.th('right','#eff6ff','#1d4ed8')}>Poids Bov.</th>
                      <th style={S.th('right','#ecfeff','#0891b2')}>Camelins</th><th style={S.th('right','#ecfeff','#0891b2')}>Poids Cam.</th>
                      <th style={S.th('right','#faf5ff','#7c3aed')}>Ovins</th><th style={S.th('right','#faf5ff','#7c3aed')}>Poids Ov.</th>
                      <th style={S.th('right','#fffbeb','#d97706')}>Caprins</th><th style={S.th('right','#fffbeb','#d97706')}>Poids Cap.</th>
                      <th style={S.th('right','#fdf2f8','#db2777')}>Porcins</th><th style={S.th('right','#fdf2f8','#db2777')}>Poids Por.</th>
                      <th style={S.th('right','#f0fdf4','#166534')}>Total Têtes</th><th style={S.th('right','#dcfce7','#14532d')}>Poids Total</th>
                    </tr></thead>
                    <tbody>
                      {lignePage.length===0
                        ? <tr><td colSpan={15} style={{...S.td(),textAlign:'center',padding:40,color:'#9ca3af'}}>📭 Aucune donnée</td></tr>
                        : lignePage.map((l,i)=>{
                          const pt=(+l.poids_bovins||0)+(+l.poids_camelins||0)+(+l.poids_ovins||0)+(+l.poids_caprins||0)+(+l.poids_porcins||0);
                          return (
                            <tr key={i} style={{background:i%2===0?'#fff':'#fafafa'}}>
                              <td style={S.td('left',null,null,600)}>{fmtDate(l.date_saisie)}</td>
                              <td style={S.td()}><span style={S.badge('#1e40af','#dbeafe')}>{l.region}</span></td>
                              <td style={S.td()}>{l.abattoir_nom}</td>
                              <td style={S.td('right','#1d4ed8',null,700)}>{fmt(l.bovins_nets)}</td>
                              <td style={S.td('right')}>{fmt(l.poids_bovins)} kg</td>
                              <td style={S.td('right','#0891b2')}>{fmt(l.camelins)}</td>
                              <td style={S.td('right')}>{fmt(l.poids_camelins||0)} kg</td>
                              <td style={S.td('right','#7c3aed',null,600)}>{fmt(l.ovins)}</td>
                              <td style={S.td('right')}>{fmt(l.poids_ovins)} kg</td>
                              <td style={S.td('right','#d97706',null,600)}>{fmt(l.caprins)}</td>
                              <td style={S.td('right')}>{fmt(l.poids_caprins)} kg</td>
                              <td style={S.td('right','#db2777')}>{fmt(l.porcins)}</td>
                              <td style={S.td('right')}>{fmt(l.poids_porcins||0)} kg</td>
                              <td style={S.td('right','#166534','#f0fdf4',800)}>{fmt(l.total_animaux)}</td>
                              <td style={S.td('right','#14532d','#dcfce7',800)}>{fmt(pt.toFixed(1))} kg</td>
                            </tr>
                          );
                        })
                      }
                      {totaux && lignes.length>0 && (
                        <tr style={{background:'#f0fdf4',borderTop:'2px solid #bbf7d0'}}>
                          <td colSpan={3} style={S.td('left',VERT,null,800)}>TOTAL GÉNÉRAL</td>
                          <td style={S.td('right','#1d4ed8',null,800)}>{fmt(totaux.bovins_nets)}</td>
                          <td style={S.td('right',null,null,700)}>{fmt(totaux.poids_bovins)} kg</td>
                          <td style={S.td('right','#0891b2',null,700)}>{fmt(totaux.camelins)}</td>
                          <td style={S.td('right',null,null,700)}>{fmt(totaux.poids_camelins||0)} kg</td>
                          <td style={S.td('right','#7c3aed',null,800)}>{fmt(totaux.ovins)}</td>
                          <td style={S.td('right',null,null,700)}>{fmt(totaux.poids_ovins)} kg</td>
                          <td style={S.td('right','#d97706',null,800)}>{fmt(totaux.caprins)}</td>
                          <td style={S.td('right',null,null,700)}>{fmt(totaux.poids_caprins)} kg</td>
                          <td style={S.td('right','#db2777',null,700)}>{fmt(totaux.porcins)}</td>
                          <td style={S.td('right',null,null,700)}>— kg</td>
                          <td style={S.td('right',VERT,'#dcfce7',800)}>{fmt(totaux.total_animaux)}</td>
                          <td style={S.td('right','#14532d','#dcfce7',800)}>{fmt(poidsTotal.toFixed(1))} kg</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination/>
              </div>
            )}

            {/* TABLE CAISSE */}
            {onglet==='caisse' && (
              <div style={S.tBox}>
                <div style={S.tHdr}>
                  <span style={S.tTit}>💰 Rapport de Caisse — {lignes.length} ligne(s)</span>
                  <span style={{fontSize:12,color:'#9ca3af'}}>Du {fmtDate(donnees.date_debut)} au {fmtDate(donnees.date_fin)}</span>
                </div>
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr>
                      <th style={S.th('left')}>Date</th><th style={S.th('left')}>Région</th><th style={S.th('left')}>Abattoir</th><th style={S.th('left')}>Caissier</th>
                      <th style={S.th('right','#f0fdf4','#059669')}>Encaissé</th><th style={S.th('right','#fef2f2','#dc2626')}>Décaissé</th>
                      <th style={S.th('right')}>Solde Net</th><th style={S.th('right')}>Billetage</th><th style={S.th('right')}>Écart</th><th style={S.th('left')}>Statut</th>
                    </tr></thead>
                    <tbody>
                      {lignePage.length===0
                        ? <tr><td colSpan={10} style={{...S.td(),textAlign:'center',padding:40,color:'#9ca3af'}}>📭 Aucune donnée</td></tr>
                        : lignePage.map((l,i)=>(
                          <tr key={i} style={{background:i%2===0?'#fff':'#fafafa'}}>
                            <td style={S.td('left',null,null,600)}>{fmtDate(l.date_rapport)}</td>
                            <td style={S.td()}><span style={S.badge('#1e40af','#dbeafe')}>{l.region}</span></td>
                            <td style={S.td()}>{l.abattoir_nom}</td>
                            <td style={S.td()}>{l.caissier_nom}</td>
                            <td style={S.td('right','#059669',null,600)}>{fmtF(l.total_encaisse)}</td>
                            <td style={S.td('right','#dc2626')}>{fmtF(l.total_decaisse)}</td>
                            <td style={S.td('right',l.solde_net>=0?'#059669':'#dc2626',null,700)}>{fmtF(l.solde_net)}</td>
                            <td style={S.td('right')}>{fmtF(l.total_billetage)}</td>
                            <td style={S.td('right',Math.abs(l.billetage_ecart||0)<5?'#92400e':'#dc2626',null,600)}>{l.billetage_ecart!==null?(l.billetage_ecart>=0?'+':'')+fmtF(l.billetage_ecart):'—'}</td>
                            <td style={S.td()}>{(()=>{const cfg={VALIDE:['#059669','#f0fdf4'],SOUMIS:['#2563eb','#eff6ff'],BROUILLON:['#6b7280','#f3f4f6'],REJETE:['#dc2626','#fef2f2']};const[c,bg]=cfg[l.statut]||['#6b7280','#f3f4f6'];return<span style={S.badge(c,bg)}>{l.statut}</span>;})()}</td>
                          </tr>
                        ))
                      }
                      {totaux && lignes.length>0 && (
                        <tr style={{background:'#f0fdf4',borderTop:'2px solid #bbf7d0'}}>
                          <td colSpan={4} style={S.td('left',VERT,null,800)}>TOTAL GÉNÉRAL</td>
                          <td style={S.td('right','#059669',null,800)}>{fmtF(totaux.total_encaisse)}</td>
                          <td style={S.td('right','#dc2626',null,800)}>{fmtF(totaux.total_decaisse)}</td>
                          <td style={S.td('right',totaux.solde_net>=0?'#059669':'#dc2626',null,800)}>{fmtF(totaux.solde_net)}</td>
                          <td colSpan={3} style={S.td()}/>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination/>
              </div>
            )}
          </>
        )}

        {!chargement && !donnees && onglet !== 'recap' && (
          <div style={{...S.tBox,...S.vide}}>
            <div style={{fontSize:48,marginBottom:12}}>📊</div>
            <div style={{fontSize:16,fontWeight:600,color:'#374151',marginBottom:6}}>Prêt à générer votre rapport</div>
            <div style={{fontSize:13,color:'#9ca3af'}}>Sélectionnez vos critères et cliquez sur "Générer"</div>
          </div>
        )}

        {/* ONGLET RÉCAPITULATIF */}
        {onglet === 'recap' && !chargement && donneesRegions && (
          <div style={S.tBox}>
            <div style={S.tHdr}>
              <div style={{display:'flex',alignItems:'center',gap:16}}>
                <span style={S.tTit}>📋 Récapitulatif par région</span>
              </div>
              <span style={{fontSize:12,color:'#9ca3af'}}>
                {vueRecap==='annee'
                  ? `Production annuelle ${(filtresRecap.date_debut||today()).slice(0,4)}`
                  : `Du ${fmtDate(donneesRegions.date_debut)} au ${fmtDate(donneesRegions.date_fin)}`}
              </span>
            </div>
            {lignesRegions.length===0 ? (
              <div style={{...S.vide,padding:32}}>Aucune donnée par région pour cette période</div>
            ) : (
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr>
                      {[
                        {label:'RÉGIONS', bg:'#1B5E20', span:0},
                        {label:'Bovins',  bg:'#1B5E20', span:2},
                        {label:'Ovins',   bg:'#1B5E20', span:2},
                        {label:'Caprins', bg:'#1B5E20', span:2},
                        {label:'Porcins', bg:'#1B5E20', span:2},
                        {label:'Camelins',bg:'#1B5E20', span:2},
                        {label:'V.F',     bg:'#1B5E20', span:0},
                        {label:'PTVT',    bg:'#1B5E20', span:0},
                      ].map(({label,bg,span},i)=>(
                        <th key={i} colSpan={span||1} rowSpan={span?1:2}
                          style={{padding:'7px 8px',fontSize:9,fontWeight:700,color:'#fff',textTransform:'uppercase',
                                  background:bg,border:'1px solid rgba(255,255,255,.2)',textAlign:'center',whiteSpace:'nowrap'}}>
                          {label}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {[['Nbre','#1B5E20'],['Poids','#1B5E20'],['Nbre','#1B5E20'],['Poids','#1B5E20'],
                        ['Nbre','#1B5E20'],['Poids','#1B5E20'],['Nbre','#1B5E20'],['Poids','#1B5E20'],
                        ['Nbre','#1B5E20'],['Poids','#1B5E20']].map(([l,c],i)=>(
                        <th key={i} style={{padding:'5px 8px',fontSize:9,fontWeight:700,color:'#fff',
                                            background:c,border:'1px solid rgba(255,255,255,.2)',textAlign:'center',whiteSpace:'nowrap'}}>
                          {l}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lignesRegions.map((l,i)=>(
                      <tr key={i} style={{background:i%2===0?'#FFFFFF':'#E8F5E9'}}>
                        <td style={{padding:'8px 10px',fontSize:12,fontWeight:700,color:'#212121',borderBottom:'1px solid #F0F2F4',whiteSpace:'nowrap'}}>{l.region}</td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:600,borderBottom:'1px solid #F0F2F4'}}>{fmt(l.nbre_bovins)}</td>
                        <td style={{padding:'8px 9px',fontSize:11,textAlign:'right',color:'#212121',borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.poids_bovins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:600,borderBottom:'1px solid #F0F2F4'}}>{fmt(l.nbre_ovins)}</td>
                        <td style={{padding:'8px 9px',fontSize:11,textAlign:'right',color:'#212121',borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.poids_ovins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:600,borderBottom:'1px solid #F0F2F4'}}>{fmt(l.nbre_caprins)}</td>
                        <td style={{padding:'8px 9px',fontSize:11,textAlign:'right',color:'#212121',borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.poids_caprins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:600,borderBottom:'1px solid #F0F2F4'}}>{fmt(l.nbre_porcins)}</td>
                        <td style={{padding:'8px 9px',fontSize:11,textAlign:'right',color:'#212121',borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.poids_porcins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:600,borderBottom:'1px solid #F0F2F4'}}>{fmt(l.nbre_camelins)}</td>
                        <td style={{padding:'8px 9px',fontSize:11,textAlign:'right',color:'#212121',borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.poids_camelins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:600,borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.viande_foraine)}</td>
                        <td style={{padding:'8px 9px',fontSize:12,textAlign:'right',color:'#212121',fontWeight:700,borderBottom:'1px solid #F0F2F4'}}>{fmtP(l.poids_total)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                      </tr>
                    ))}
                    <tr style={{background:'#2E7D32'}}>
                      <td style={{padding:'9px 10px',fontSize:12,fontWeight:800,color:'#FFFFFF',whiteSpace:'nowrap'}}>TOTAL DE LA PÉRIODE</td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmt(totRegions.nbre_bovins)}</td>
                      <td style={{padding:'9px 9px',fontSize:11,textAlign:'right',color:'#FFFFFF',fontWeight:700}}>{fmtP(totRegions.poids_bovins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmt(totRegions.nbre_ovins)}</td>
                      <td style={{padding:'9px 9px',fontSize:11,textAlign:'right',color:'#FFFFFF',fontWeight:700}}>{fmtP(totRegions.poids_ovins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmt(totRegions.nbre_caprins)}</td>
                      <td style={{padding:'9px 9px',fontSize:11,textAlign:'right',color:'#FFFFFF',fontWeight:700}}>{fmtP(totRegions.poids_caprins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmt(totRegions.nbre_porcins)}</td>
                      <td style={{padding:'9px 9px',fontSize:11,textAlign:'right',color:'#FFFFFF',fontWeight:700}}>{fmtP(totRegions.poids_porcins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmt(totRegions.nbre_camelins)}</td>
                      <td style={{padding:'9px 9px',fontSize:11,textAlign:'right',color:'#FFFFFF',fontWeight:700}}>{fmtP(totRegions.poids_camelins)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmtP(totRegions.viande_foraine)}</td>
                      <td style={{padding:'9px 9px',fontSize:12,textAlign:'right',color:'#FFFFFF',fontWeight:800}}>{fmtP(totRegions.poids_total)}<span style={{fontSize:9,marginLeft:2}}>kg</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {onglet === 'recap' && !chargement && !donneesRegions && (
          <div style={{...S.tBox,...S.vide}}>
            <div style={{fontSize:48,marginBottom:12}}>📋</div>
            <div style={{fontSize:16,fontWeight:600,color:'#374151',marginBottom:6}}>Récapitulatif par région</div>
            <div style={{fontSize:13,color:'#9ca3af'}}>Sélectionnez vos critères et cliquez sur "Générer"</div>
          </div>
        )}

      </div>
    </div>
  );
}
